'use client';

import React from 'react';

type Row = {
  name: string;
  platform: string;
  approach: string;
  strengths: string[];
  weakness: string;
  color: string;
};

const rows: Row[] = [
  {
    name: 'MRI Software',
    platform: 'Agora / a2i',
    approach: 'Embedded across 15+ products',
    strengths: ['Lease abstraction (patented OCR)', 'Forms intelligence', 'Ask Agora NLP'],
    weakness: 'Fragmented; retrofit onto legacy Voyager codebase',
    color: '#A78BFA',
  },
  {
    name: 'AppFolio',
    platform: 'Realm-X Performers',
    approach: 'Agentic AI for leasing + maintenance',
    strengths: ['Image-based maintenance diagnosis', '73% lead-to-showing conversion', 'FolioSpace resident UI'],
    weakness: 'Residential focused; weaker in commercial',
    color: '#22C55E',
  },
  {
    name: 'Yardi',
    platform: 'Virtuoso AI Platform',
    approach: 'Marketplace of agents + Composer (no-code builder)',
    strengths: ['MCP connectors with Claude', 'Virtuoso Verified invoices (60% AP speedup)', 'Close 20h → 5h'],
    weakness: 'Requires walled Yardi ecosystem',
    color: '#F59E0B',
  },
  {
    name: 'JLL / CBRE',
    platform: 'Custom enterprise AI',
    approach: 'In-house models for underwriting & facilities',
    strengths: ['CBRE deployed at 20,000+ sites', '1B sqft coverage', 'Proprietary data moats'],
    weakness: 'Not available as SaaS',
    color: '#EF4444',
  },
  {
    name: 'Bryckel.AI',
    platform: 'AI-first lease knowledge base',
    approach: 'Pure-play AI lease abstraction',
    strengths: ['90%+ extraction accuracy', 'Fast onboarding'],
    weakness: 'Narrow scope (leases only)',
    color: '#06B6D4',
  },
];

export default function AICompetitive() {
  return (
    <section className="py-24 px-6 relative">
      <div className="max-w-6xl mx-auto">
        <div className="reveal-blur mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[rgba(124,58,237,0.25)] mb-5">
            <span className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase">
              Competitive Landscape
            </span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}
          >
            No single vendor owns the
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              full AI-native experience.
            </span>
          </h2>
          <p className="mt-4 text-[#9999A8] max-w-3xl leading-relaxed">
            Yardi has the best data platform. AppFolio has the best agentic leasing. MRI has the best
            document intelligence. A new entrant designed for agentic workflows <span className="text-[#EEEEF0]">end-to-end</span> has a real opening.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger-children">
          {rows.map((r) => (
            <div
              key={r.name}
              className="reveal-blur bento-card card-shine p-6 flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-manrope font-extrabold text-xl text-[#EEEEF0] tracking-tight">
                    {r.name}
                  </div>
                  <div className="text-[10px] font-mono mt-1" style={{ color: r.color }}>
                    {r.platform}
                  </div>
                </div>
                <div
                  className="w-2 h-2 rounded-full mt-2 shrink-0"
                  style={{ background: r.color, boxShadow: `0 0 12px ${r.color}` }}
                />
              </div>

              <p className="text-xs font-mono text-[#9999A8] leading-relaxed">{r.approach}</p>

              <div className="h-px bg-[rgba(255,255,255,0.05)]" />

              <div>
                <div className="text-[10px] font-mono text-[#5A5A6E] tracking-widest uppercase mb-2">
                  Strengths
                </div>
                <ul className="flex flex-col gap-1.5">
                  {r.strengths.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-xs text-[#EEEEF0]">
                      <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: r.color }} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="text-[10px] font-mono text-[#5A5A6E] tracking-widest uppercase mb-2">
                  Gap
                </div>
                <div className="text-xs text-[#9999A8] leading-relaxed">{r.weakness}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
